'use client';

import { useEffect, useRef } from 'react';
import getQuantity from '@helpers/productHelper';
import QuantityStepper from '@molecules/ProductQuantityControl/QuantityStepper';
import { StepperVariant } from '@molecules/ProductQuantityControl/types';
import useAddProductQueue from '@hooks/useAddProductQueue';
import useCart from '@hooks/useCart';
import { useAppSelector } from '@hooks/useAppDispatch';
import { selectCartIsUpdating } from '@slices/cartUpdatingSlice';
import getPickUnit from '@helpers/getPickUnit';
import useProductQuantityHelper from '@helpers/useProductQuantityHelper';
import useQuantityStepper from './useQuantityStepper';

export type ProductQuantityControlVariant = 'product' | 'cart' | 'minicart' | 'wishlist';

// Product is typed as any because consumers pass different API types
// (AxfoodBasicProductViewModel, AxfoodCartProductViewModel, AxfoodProductDetailsViewModel, GraphQL product).
// The component uses: code, productBasketType?.code, unit?.code, quantityInRefOrder,
// averageWeight, incrementValue, outOfStock, bargainProduct.
interface Props {
  product: any;
  variant: ProductQuantityControlVariant;
  category?: string;
  quantityCallback?: (value: number) => void;
  mixmatch?: boolean;
  trackingSource?: string;
  quantity?: number;
  hideWhenEmpty?: boolean;
}

const ProductQuantityControl = ({
  product,
  variant,
  category = '',
  quantityCallback,
  mixmatch,
  trackingSource,
  quantity,
  hideWhenEmpty = false,
}: Props) => {
  const { cart, hasFetchedCart } = useCart();
  const { addToCart } = useAddProductQueue();
  const cartIsUpdating = useAppSelector(selectCartIsUpdating);
  // Only disable while the cart is saving if this control is the thing writing to it. The wishlist
  // variant has its own queue, and a given `quantity` means the parent owns the value.
  const busy = variant !== 'wishlist' && quantity === undefined && cartIsUpdating;
  const unit = getPickUnit(product?.productBasketType?.code);

  const getTrackingSource = (productCategory: string) => {
    if (trackingSource) return trackingSource;
    if (variant === 'cart') return 'cart';
    if (variant === 'minicart') return 'minicart';
    if (mixmatch) return `multi_product_offer | ${product.code}`;
    return productCategory;
  };

  const submitQuantity = (newQuantity: number, prevQuantity: number) => {
    const source = getTrackingSource(category);
    // Controlled mode (parent owns `quantity`): only report the change and let the parent
    // decide what to do. Uncontrolled mode: persist to the debounced cart queue.
    if (typeof quantity === 'undefined') {
      addToCart({ product, newQuantity, prevQuantity, trackingSource: source, variant });
    }
    if (quantityCallback) quantityCallback(newQuantity);
  };

  // Held in a ref to break a cycle: useProductQuantityHelper needs currentQuantity, which the
  // stepper hook below owns. maxGuard reads it lazily, once the value exists.
  const hasReachedMaxQuantityRef = useRef<(qty?: number) => boolean>(() => false);

  const {
    inputRef,
    currentQuantity,
    setCurrentQuantity,
    isActive,
    onClickHandler,
    onBlurHandler,
    onChangeHandler,
    onFocusHandler,
    onKeyUpHandler,
    onKeyDownHandler,
  } = useQuantityStepper({
    unit,
    onQuantityChange: submitQuantity,
    maxGuard: (value) => hasReachedMaxQuantityRef.current(value),
  });

  const { shouldEnableWhenOpenOrder, preventIncrementDueToLowStock, hasReachedMaxQuantity } = useProductQuantityHelper({
    cart,
    quantityInRefOrder: product.quantityInRefOrder,
    currentQuantity,
    inCartUnit: product.unit?.code || '',
    unit,
    averageWeight: product.averageWeight,
  });
  // Refs may not be written during render, so this is assigned after commit. maxGuard only runs
  // from event handlers, which means it always sees the value from the last commit.
  useEffect(() => {
    hasReachedMaxQuantityRef.current = hasReachedMaxQuantity;
  });

  useEffect(() => {
    if (quantity) {
      setCurrentQuantity(quantity);
    } else if (hasFetchedCart && cart?.products?.length) {
      const cartProduct = cart.products.find((item: any) => item.code === product.code);
      setCurrentQuantity(cartProduct ? getQuantity(cartProduct) : 0);
    } else {
      setCurrentQuantity(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFetchedCart, cart?.products]);

  const stepperVariants: Record<ProductQuantityControlVariant, StepperVariant> = {
    product: 'product',
    cart: 'cart',
    minicart: 'cart',
    wishlist: 'wishlist',
  };

  return (
    <QuantityStepper
      variant={stepperVariants[variant]}
      inputRef={inputRef}
      onBlurHandler={onBlurHandler}
      onChangeHandler={onChangeHandler}
      onFocusHandler={onFocusHandler}
      onKeyUpHandler={onKeyUpHandler}
      onKeyDownHandler={onKeyDownHandler}
      onClickHandler={onClickHandler}
      isActive={isActive}
      currentQuantity={currentQuantity}
      unit={unit}
      incrementValue={product.incrementValue}
      outOfStock={product.outOfStock}
      bargainProduct={product.bargainProduct}
      busy={busy}
      minicart={variant === 'minicart'}
      hideWhenEmpty={hideWhenEmpty}
      shouldEnableWhenOpenOrder={shouldEnableWhenOpenOrder}
      preventIncrementDueToLowStock={preventIncrementDueToLowStock}
    />
  );
};

export default ProductQuantityControl;
